import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import { IAssessmentResource } from '../../entities/farms.entities';
import {
  parseImageUrlToFileItem,
  defaultDateFormat,
  getUnitOfSpecie,
} from '../../lib/common.helpers';
import { Button, CloseIcon, Input, Subtitle } from '../shared';
import { customFieldsValues } from '../../entities/util-functions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  selectAllFarms,
  SelectAssessFields,
} from '../../store/extra/extra.selector';
import { selectAccount } from '../../store/auth/auth.selector';
import FilesWall from '../shared/pictures-wall/FilesWall';
import { AssessmentSizeRange } from '../CommonViews';

interface Props {
  visible: boolean;
  assessment: IAssessmentResource;
  lineId: number;
  onClose: () => void;
}

const AssessmentViewModal = ({
  visible,
  assessment,
  lineId,
  onClose,
}: Props) => {
  const lang = useSelector(selectLang);
  const accSetting = useSelector(selectAccount);
  const unitType = accSetting?.display_unit;
  const manageWidth = !!accSetting?.manage_shellfish_width;
  const farmsData = useSelector(selectAllFarms);
  const farm = farmsData.find(x => x.lines.some(y => y.id === lineId));
  const line = farm?.lines.find(x => x.id === lineId);

  const customFields = SelectAssessFields(farm?.id);
  const customValues = customFieldsValues(
    customFields,
    assessment.custom_values,
  );

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      width={600}
      footer={null}
    >
      <div className='wrap view-modal'>
        <div className='d-flex align-center mb-16'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Assessment Details')}:
          </Subtitle>
          <Subtitle
            className='ml-7'
            color='black-3'
            align='left'
            size={1}
            fontWeight={500}
          >
            {`${farm?.name} - ${line?.line_name}`}
          </Subtitle>
        </div>
        <div className='pt-16 pb-24'>
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>
                {manageWidth
                  ? translate(lang, 'Length')
                  : translate(lang, 'Size')}
              </div>
              <div className='info-value'>
                <AssessmentSizeRange data={assessment} />
              </div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>
                {translate(lang, 'Assessment date')}
              </div>
              <div className='info-value'>
                {defaultDateFormat(assessment.assessment_date)}
              </div>
            </div>
          </div>
          {manageWidth &&
            (assessment.min_width != null ||
              assessment.max_width != null ||
              assessment.avg_width != null) && (
              <div className='d-flex'>
                <div className='info-card mr-17'>
                  <div className='info-label'>
                    {translate(lang, 'Width')}
                  </div>
                  <div className='info-value'>
                    {`${assessment.min_width ?? '-'} ~ ${assessment.max_width ?? '-'}, ${assessment.avg_width ?? '-'} ${getUnitOfSpecie(unitType, 'size_mm')}`}
                  </div>
                </div>
              </div>
            )}
          <div className='d-flex'>
            <div className='info-card mr-17'>
              <div className='info-label'>
                {translate(lang, 'Planned harvest date')}
              </div>
              <div className='info-value'>
                {defaultDateFormat(assessment.planned_date_harvest)}
              </div>
            </div>
            <div className='info-card ml-17'>
              <div className='info-label'>
                {translate(lang, 'Ready to harvest')}
              </div>
              <div className='info-value'>
                {!assessment.ready_harvest
                  ? 'No'
                  : defaultDateFormat(assessment.ready_harvest)}
              </div>
            </div>
          </div>
          <div className='d-flex justify-between' style={{ flexWrap: 'wrap' }}>
            {customValues?.map(x => (
              <div
                key={x.id}
                className='info-card'
                style={{ flexBasis: '47%', marginLeft: 5, marginRight: 5 }}
              >
                <div className='info-label'>{x.label}</div>
                <div className='info-value'>
                  {`${x.value ?? ''} ${x.unit ?? ''}`}
                </div>
              </div>
            ))}
          </div>
          <div className='mt-7 mb-7'>
            <Input
              label={translate(lang, 'Comment')}
              type='textarea'
              value={assessment.comment ?? ''}
              disabled={true}
            />
          </div>
          {!!assessment.image_pdf_files &&
            assessment.image_pdf_files.length > 0 && (
              <div className='mt-7 mb-7'>
                <FilesWall
                  label={translate(lang, 'Images')}
                  fileList={
                    assessment.image_pdf_files.map(x =>
                      parseImageUrlToFileItem(x),
                    ) as any
                  }
                  disabled={true}
                />
              </div>
            )}
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={onClose}
          >
            {translate(lang, 'Close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssessmentViewModal;
